/**
 * Upload Storage Service
 *
 * Provides IndexedDB-based storage for:
 * - Photo blobs (persisted across app restarts)
 * - Upload queue state (resumable uploads)
 *
 * Uses the 'idb' library for simplified IndexedDB operations.
 */
import { openDB } from 'idb';
const DB_NAME = 'cfn_uploads';
const DB_VERSION = 1;
let dbInstance = null;
/**
 * Get or initialise the database connection
 */
export async function getDb() {
    if (dbInstance)
        return dbInstance;
    dbInstance = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
            // Photo blobs store
            if (!db.objectStoreNames.contains('cfn_photo_blobs')) {
                db.createObjectStore('cfn_photo_blobs', { keyPath: 'fileId' });
            }
            // Upload queue store
            if (!db.objectStoreNames.contains('cfn_upload_queue')) {
                const queueStore = db.createObjectStore('cfn_upload_queue', { keyPath: 'fileId' });
                queueStore.createIndex('by-status', 'status');
                queueStore.createIndex('by-created', 'createdAt');
            }
        },
    });
    return dbInstance;
}
// ============ Blob Operations ============
/**
 * Save a file to IndexedDB as ArrayBuffer
 *
 * Safari/WebKit has a long-standing bug where storing Blobs in IndexedDB can
 * fail with "BlobURLs are not yet supported" or similar DataCloneErrors.
 * The reliable workaround is to store the raw ArrayBuffer instead, and
 * reconstruct the Blob when reading. This works on all browsers.
 */
export async function saveBlob(fileId, file) {
    const db = await getDb();
    // Read file as ArrayBuffer - this is the Safari-compatible approach
    // that works reliably across all browsers and iOS versions
    const arrayBuffer = await file.arrayBuffer();
    await db.put('cfn_photo_blobs', {
        fileId,
        data: arrayBuffer, // Store raw ArrayBuffer, not Blob
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        createdAt: new Date().toISOString(),
    });
}
/**
 * Get a blob from IndexedDB
 * Reconstructs the Blob from the stored ArrayBuffer
 */
export async function getBlob(fileId) {
    const db = await getDb();
    const record = await db.get('cfn_photo_blobs', fileId);
    if (!record)
        return null;
    // Handle both old format (blob) and new format (data: ArrayBuffer)
    // for backward compatibility with any existing stored data
    if ('blob' in record && record.blob instanceof Blob) {
        return record.blob;
    }
    // Reconstruct Blob from ArrayBuffer
    return new Blob([record.data], { type: record.type });
}
/**
 * Delete a blob from IndexedDB
 */
export async function deleteBlob(fileId) {
    const db = await getDb();
    await db.delete('cfn_photo_blobs', fileId);
}
/**
 * Get blob metadata without loading the full data
 * Handles both old format (blob) and new format (data: ArrayBuffer) for backward compatibility
 */
export async function getBlobMetadata(fileId) {
    const db = await getDb();
    const record = await db.get('cfn_photo_blobs', fileId);
    if (!record)
        return null;
    // Return metadata without the binary data (works for both old and new format)
    // Old format has 'blob', new format has 'data'
    const { blob, data, ...metadata } = record;
    return metadata;
}
// ============ Queue Operations ============
/**
 * Add an item to the upload queue
 */
export async function addToQueue(item) {
    const db = await getDb();
    await db.put('cfn_upload_queue', item);
}
/**
 * Update an existing queue item
 */
export async function updateQueueItem(fileId, updates) {
    const db = await getDb();
    const existing = await db.get('cfn_upload_queue', fileId);
    if (existing) {
        await db.put('cfn_upload_queue', {
            ...existing,
            ...updates,
            updatedAt: new Date().toISOString(),
        });
    }
}
/**
 * Get a queue item by fileId
 */
export async function getQueueItem(fileId) {
    const db = await getDb();
    return db.get('cfn_upload_queue', fileId);
}
/**
 * Get all pending items (queued, paused, uploading, needs_reselect)
 */
export async function getPendingItems() {
    const db = await getDb();
    const items = await db.getAll('cfn_upload_queue');
    return items.filter(item => ['queued', 'paused', 'uploading', 'failed', 'needs_reselect'].includes(item.status));
}
/**
 * Get all completed items
 */
export async function getCompletedItems() {
    const db = await getDb();
    const items = await db.getAll('cfn_upload_queue');
    return items.filter(item => item.status === 'completed');
}
/**
 * Get all queue items
 */
export async function getAllQueueItems() {
    const db = await getDb();
    return db.getAll('cfn_upload_queue');
}
/**
 * Remove an item from the queue (and its blob)
 */
export async function removeFromQueue(fileId) {
    const db = await getDb();
    await db.delete('cfn_upload_queue', fileId);
    await db.delete('cfn_photo_blobs', fileId);
}
/**
 * Clear completed items older than specified age
 */
export async function clearCompletedItems(olderThanMs = 86400000) {
    const db = await getDb();
    const items = await getCompletedItems();
    const cutoff = Date.now() - olderThanMs;
    let deletedCount = 0;
    for (const item of items) {
        const completedAt = new Date(item.updatedAt).getTime();
        if (completedAt < cutoff) {
            await removeFromQueue(item.fileId);
            deletedCount++;
        }
    }
    return deletedCount;
}
// ============ Storage Management ============
/**
 * Request persistent storage from the browser
 * This helps prevent IndexedDB data from being evicted
 */
export async function requestPersistentStorage() {
    if (navigator.storage && navigator.storage.persist) {
        try {
            const persisted = await navigator.storage.persist();
            console.log('[UploadStorage] Persistent storage:', persisted ? 'granted' : 'denied');
            return persisted;
        }
        catch (err) {
            console.warn('[UploadStorage] Failed to request persistent storage:', err);
            return false;
        }
    }
    return false;
}
/**
 * Check storage quota
 */
export async function checkStorageQuota() {
    if (navigator.storage && navigator.storage.estimate) {
        try {
            const estimate = await navigator.storage.estimate();
            const used = estimate.usage || 0;
            const quota = estimate.quota || 0;
            return {
                used,
                available: quota - used,
                percentUsed: quota > 0 ? (used / quota) * 100 : 0,
            };
        }
        catch (err) {
            console.warn('[UploadStorage] Failed to check storage quota:', err);
        }
    }
    return { used: 0, available: 0, percentUsed: 0 };
}
/**
 * Check if persistent storage is currently granted
 */
export async function isPersisted() {
    if (navigator.storage && navigator.storage.persisted) {
        try {
            return await navigator.storage.persisted();
        }
        catch (err) {
            console.warn('[UploadStorage] Failed to check persistence status:', err);
        }
    }
    return false;
}
// ============ Utility Functions ============
/**
 * Create a new queue item with default values
 */
export function createQueueItem(fileId, file, objectType, objectId, options = {}) {
    const now = new Date().toISOString();
    return {
        fileId,
        objectType,
        objectId,
        filename: options.filename || file.name,
        mimetype: file.type,
        size: file.size,
        status: 'queued',
        tusUrl: null,
        bytesUploaded: 0,
        bytesTotal: file.size,
        browserLocation: options.browserLocation || null,
        uploadId: null,
        errorCode: null,
        errorMessage: null,
        retryCount: 0,
        createdAt: now,
        updatedAt: now,
        startedAt: null,
        completedAt: null,
        effectiveBitrate: null,
    };
}
/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes) {
    if (bytes === 0)
        return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
/**
 * Generate a structured filename for an upload
 */
function generateFilename(file, objectType, objectId) {
    const prefix = objectType === 'UPRN' ? 'UPRN' : 'structure';
    const extMap = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/heic': 'heic',
        'image/heif': 'heif',
    };
    const ext = extMap[file.type] ||
        file.name.match(/\.([a-zA-Z0-9]+)$/)?.[1] ||
        'bin';
    return `${prefix}_${objectId}_${file.lastModified}.${ext}`;
}
/**
 * Add files to the upload queue
 *
 * This is the main entry point for adding files from any context (popup or main app).
 * It handles:
 * - Requesting persistent storage
 * - Saving blobs to IndexedDB
 * - Creating queue items
 * - Dispatching event to notify the upload processor
 *
 * The actual upload processing is handled separately by useUploadQueue in the main app.
 */
export async function addFilesToQueue(files, context) {
    console.log('[UploadStorage] Adding files to queue:', files.length);
    // Request persistent storage on first upload
    await requestPersistentStorage();
    const fileIds = [];
    for (const file of files) {
        // Generate unique ID using crypto.randomUUID (available in modern browsers)
        const fileId = crypto.randomUUID();
        const filename = generateFilename(file, context.objectType, context.objectId);
        // Save blob to IndexedDB
        await saveBlob(fileId, file);
        // Create queue item
        const item = createQueueItem(fileId, file, context.objectType, context.objectId, {
            filename,
            browserLocation: context.browserLocation,
        });
        await addToQueue(item);
        fileIds.push(fileId);
        console.log('[UploadStorage] File queued:', fileId, filename);
    }
    // Dispatch event to notify the upload processor (main app)
    window.dispatchEvent(new CustomEvent('cfn:upload:queued', {
        detail: { count: files.length, fileIds }
    }));
    return fileIds;
}
const UPLOAD_MODE_KEY = 'cfn_upload_mode';
/**
 * Check if Network Information API is available with connection type support
 */
export function hasNetworkTypeSupport() {
    const connection = navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
    return !!(connection && 'type' in connection);
}
/**
 * Get current network connection type
 * Returns 'wifi', 'cellular', 'ethernet', 'none', 'unknown', etc.
 */
export function getConnectionType() {
    const connection = navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
    return connection?.type ?? null;
}
/**
 * Check if current connection is WiFi
 */
export function isOnWifi() {
    return getConnectionType() === 'wifi';
}
/**
 * Check if current connection is cellular/metered
 */
export function isOnCellular() {
    const type = getConnectionType();
    return type === 'cellular' || type === 'wimax';
}
/**
 * Get the saved upload mode preference
 */
export function getUploadMode() {
    try {
        const saved = localStorage.getItem(UPLOAD_MODE_KEY);
        if (saved === 'immediate' || saved === 'hold' || saved === 'wifi-only') {
            // Validate wifi-only is still supported
            if (saved === 'wifi-only' && !hasNetworkTypeSupport()) {
                return 'immediate';
            }
            return saved;
        }
    }
    catch (err) {
        console.warn('[UploadStorage] Failed to read upload mode:', err);
    }
    return 'immediate';
}
/**
 * Save the upload mode preference
 */
export function setUploadMode(mode) {
    try {
        localStorage.setItem(UPLOAD_MODE_KEY, mode);
        // Dispatch event so useUploadQueue can react
        window.dispatchEvent(new CustomEvent('cfn:upload:mode-changed', {
            detail: { mode }
        }));
    }
    catch (err) {
        console.warn('[UploadStorage] Failed to save upload mode:', err);
    }
}
/**
 * Check if uploads should proceed based on current mode and network state
 */
export function shouldUploadNow() {
    const mode = getUploadMode();
    switch (mode) {
        case 'immediate':
            return true;
        case 'hold':
            return false;
        case 'wifi-only':
            if (!hasNetworkTypeSupport()) {
                // Fallback to immediate if API not available
                return true;
            }
            return isOnWifi();
        default:
            return true;
    }
}
/**
 * Get available upload modes for the current device
 */
export function getAvailableUploadModes() {
    const modes = [
        {
            mode: 'immediate',
            label: 'Upload immediately',
            description: 'Photos upload as soon as they are taken',
        },
        {
            mode: 'hold',
            label: 'Hold all uploads',
            description: 'Queue photos but wait for manual upload',
        },
    ];
    // Only offer WiFi option if the device supports network type detection
    if (hasNetworkTypeSupport()) {
        modes.push({
            mode: 'wifi-only',
            label: 'WiFi only',
            description: 'Only upload when connected to WiFi',
        });
    }
    return modes;
}
const MAX_BITRATE_KEY = 'cfn_max_bitrate';
/**
 * Available max bitrate options for UI
 */
export const MAX_BITRATE_OPTIONS = [
    { value: 0, label: 'Unlimited' },
    { value: 500000, label: '500 Kbps' },
    { value: 1000000, label: '1 Mbps' },
    { value: 2000000, label: '2 Mbps' },
    { value: 5000000, label: '5 Mbps' },
];
/**
 * Get the saved max bitrate preference
 */
export function getMaxBitrate() {
    try {
        const saved = localStorage.getItem(MAX_BITRATE_KEY);
        if (saved) {
            const value = parseInt(saved, 10);
            if (MAX_BITRATE_OPTIONS.some(opt => opt.value === value)) {
                return value;
            }
        }
    }
    catch (err) {
        console.warn('[UploadStorage] Failed to read max bitrate:', err);
    }
    return 0; // Default to unlimited
}
/**
 * Save the max bitrate preference
 */
export function setMaxBitrate(bitrate) {
    try {
        localStorage.setItem(MAX_BITRATE_KEY, String(bitrate));
        // Dispatch event so useUploadQueue can react
        window.dispatchEvent(new CustomEvent('cfn:upload:bitrate-changed', {
            detail: { bitrate }
        }));
    }
    catch (err) {
        console.warn('[UploadStorage] Failed to save max bitrate:', err);
    }
}
/**
 * Format bitrate to human readable string
 */
export function formatBitrate(bitsPerSecond) {
    if (bitsPerSecond === 0)
        return 'Unlimited';
    if (bitsPerSecond >= 1000000) {
        return (bitsPerSecond / 1000000).toFixed(1) + ' Mbps';
    }
    if (bitsPerSecond >= 1000) {
        return (bitsPerSecond / 1000).toFixed(0) + ' Kbps';
    }
    return bitsPerSecond + ' bps';
}
/**
 * Calculate delay needed to achieve target bitrate
 * Returns milliseconds to wait before next chunk
 */
export function calculateThrottleDelay(bytesTransferred, elapsedMs, maxBitrate) {
    if (maxBitrate === 0)
        return 0; // No throttling
    const bitsTransferred = bytesTransferred * 8;
    const targetTimeMs = (bitsTransferred / maxBitrate) * 1000;
    const delay = targetTimeMs - elapsedMs;
    return Math.max(0, delay);
}
// ============ Bulk Upload Support ============
/**
 * Filename pattern for bulk uploads - matches server-side pattern
 * Format: {prefix}_{objectId}_{timestamp}.{ext}
 */
const BULK_FILENAME_PATTERN = /^(jb|mh|pole|structure|UPRN)_([a-zA-Z0-9]+)_([0-9]+)\.(jpg|jpeg|png)$/i;
/**
 * Parse object type and ID from a bulk upload filename
 */
export function parseFilenameForBulkUpload(filename) {
    const match = filename.match(BULK_FILENAME_PATTERN);
    if (!match)
        return null;
    const [, prefix, objectId, timestamp] = match;
    // Legacy prefixes (jb, mh, pole) map to 'structure'
    const objectType = ['jb', 'mh', 'pole', 'structure'].includes(prefix.toLowerCase())
        ? 'structure'
        : 'UPRN';
    return {
        objectType,
        objectId,
        timestamp: parseInt(timestamp, 10),
    };
}
/**
 * Add a completed bulk upload to the queue as a "completed" item
 * This allows bulk uploads to appear in the upload history with thumbnails
 */
export async function addCompletedBulkUpload(upload) {
    const parsed = parseFilenameForBulkUpload(upload.filename);
    if (!parsed) {
        console.warn('[UploadStorage] Could not parse bulk upload filename:', upload.filename);
        return null;
    }
    const now = new Date().toISOString();
    const fileId = crypto.randomUUID();
    const item = {
        fileId,
        objectType: parsed.objectType,
        objectId: parsed.objectId,
        filename: upload.filename,
        mimetype: upload.mimetype,
        size: upload.size,
        status: 'completed',
        tusUrl: null,
        bytesUploaded: upload.size,
        bytesTotal: upload.size,
        browserLocation: null,
        uploadId: upload.id,
        errorCode: null,
        errorMessage: null,
        retryCount: 0,
        createdAt: now,
        updatedAt: now,
        startedAt: null,
        completedAt: Date.now(),
        effectiveBitrate: null, // Not tracked for bulk uploads
    };
    await addToQueue(item);
    console.log('[UploadStorage] Added completed bulk upload:', fileId, upload.filename);
    return fileId;
}
/**
 * Add multiple completed bulk uploads to the queue
 * Returns array of fileIds for successfully added items
 */
export async function addCompletedBulkUploads(uploads) {
    const fileIds = [];
    for (const upload of uploads) {
        const fileId = await addCompletedBulkUpload(upload);
        if (fileId) {
            fileIds.push(fileId);
        }
    }
    // Dispatch event to notify listeners (e.g., UploadHistoryPage)
    if (fileIds.length > 0) {
        window.dispatchEvent(new CustomEvent('cfn:upload:complete', {
            detail: { count: fileIds.length, fileIds, source: 'bulk' }
        }));
    }
    return fileIds;
}
